import { mapState, mapGetters, mapActions } from 'vuex';
import VueFormGenerator from 'vue-form-generator';
import pluralize from 'pluralize';

export default {
  data: () => ({
    formSchema: {
      fields: [],
    },
    formModel: {},
    formModelChanged: false,
  }),

  computed: {
    ...mapState({
      inputTypes: state => state.field.inputTypes,
      surveyResponse: state => state.survey.surveyResponse,
    }),

    ...mapState([
      'formOptions',
    ]),

    ...mapGetters([
      'activeFields',
      'activePage',
      'inAdminArea',
      'onFirstPage',
      'onLastPage',
    ]),
  },

  watch: {
    activeFields: {
      handler() {
        if (this.activeFields) {
          this.buildForm(this.formBuilderOptions || {});
        }
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions([
      'savePage',
    ]),

    buildForm(options = {}) {
      this.formModelChanged = false;
      let userFields = [];
      if (!this.onFirstPage) {
        userFields = userFields.concat([
          this.buildField({
            title: '',
            buttonText: 'Previous Page',
            field_type: 'button',
            onClick: () => { this.goToPage(this.previousPage); },
            styleClasses: 'form__field--button form__field--button-50 text-center',
          }, options),
        ]);
      }
      userFields = userFields.concat([
        this.buildField({
          title: '',
          buttonText: this.onLastPage ? 'Submit Survey' : 'Next Page',
          field_type: 'submit',
          styleClasses: `form__field--button ${!this.onFirstPage ? 'form__field--button-50' : null}`,
        }, options),
      ]);
      this.formSchema.fields = this.inAdminArea ? [] : userFields;
      let questionNumber = 1;
      this.activeFields.forEach((field, index) => {
        field.first = index === 0;
        field.last = index === this.activeFields.length - 1;
        field.index = index;
        if (field.field_type !== 'statictext') {
          field.questionNumber = questionNumber;
          questionNumber += 1;
        }
        this.formSchema.fields.splice(index, 0, this.buildField(field, options));
      });
      if (this.$refs.vfg) this.$refs.vfg.$on('model-updated', () => this.formModelChanged = true);
      this.$emit('formBuilt', true);
    },

    buildField(field, options) {
      const isStaticText = field.field_type === 'statictext';
      const label = isStaticText ? '' : `${field.questionNumber}. ${field.title}`;
      let styleClasses = 'form__field form__field--survey';
      if (options.last && options.index % 2 === 0) styleClasses = styleClasses.concat(' form__field--last');
      if (field.field_type === 'file') styleClasses = styleClasses.concat(' form__field--full');
      styleClasses = styleClasses.concat(` ${field.styleClasses}`);

      return {
        ...this.inputTypes[field.field_type].vfg,
        styleClasses,
        label,
        model: field.ref,
        values: field.options ? field.options.map((option) => {
          return {
            id: option.value,
            name: option.text,
            value: option.value,
            originalValue: option.value,
            hasInputBox: option.has_input_box,
            ref: option.ref,
          };
        }) : [],
        min: field.min_length,
        max: field.max_length,
        minlength: field.min_length,
        maxlength: field.max_length,
        minvalue: field.min_value,
        maxvalue: field.max_value,
        stepSize: field.step_size,
        default: field.default,
        required: isStaticText ? false : field.required,
        pattern: field.validation,
        validator: this.validations(field),
        placeholder: field.placeholder,
        hint: field.hint,
        startLabel: field.start_label,
        endLabel: field.end_label,
        disabled: field.disabled !== undefined ? field.disabled : options.disabled,
        buttonText: field.buttonText,
        fieldId: field.id,
        fieldPosition: field.position,
        firstField: field.first,
        lastField: field.last,
        pageId: field.page_id,
        staticText: field.parsed_static_text,
        maxOptionSelections: field.max_option_selections,
        showActions: options.editMode,
        surveyArchived: options.surveyArchived,
        validateBeforeSubmit: true,
        onClick: (model, schema, context, form) => {
          field.onClick(model, schema, context, form);
        },
        onSubmit: (model, schema, context, form) => {
          this.handleSubmit(model, schema, context, form, false, !this.onLastPage);
        },
      };
    },

    validations(field) {
      const validations = [];

      if (field.required) {
        validations.push('required');
      }

      if (field.validation) {
        validations.push(VueFormGenerator.validators.regexp.locale({
          invalidFormat: field.regex_hint || 'Invalid Format',
        }));
      }

      if (['text', 'textarea', 'datetime', 'dropdown'].includes(field.field_type)) {
        validations.push('string');
      }

      if (['integer', 'opinionscale'].includes(field.field_type)) {
        validations.push(VueFormGenerator.validators.number);
      }

      if (['checkbox', 'opinionrange'].includes(field.field_type)) {
        validations.push(VueFormGenerator.validators.array.locale({
          thisNotArray: 'You need to select at least one option',
        }));
      }

      if (field.field_type === 'checkbox') {
        validations.push((value, formField) => {
          if (
            formField.maxOptionSelections &&
            value !== undefined &&
            (formField.maxOptionSelections === 0 || value.length > formField.maxOptionSelections)
          ) {
            return [`You can only select ${pluralize('option', formField.maxOptionSelections, true)}`];
          }
          return [];
        });
      }

      if (field.field_type === 'boolean') {
        validations.push((value, formField) => {
          if (formField.required && !value) {
            return ['You need to select this'];
          }
          return [];
        });
      }

      if (field.field_type === 'multipletext') {
        validations.push((value, formField) => {
          if (formField.required && typeof value !== 'object') {
            return ['You need to fill in at least one of these fields'];
          }
          return [];
        });
      }

      if (field.field_type === 'emailaddress' && !field.validation) {
        validations.push('email');
      }

      return validations;
    },

    handleSubmit(model, schema, context, form, skipPageChange = false, skipComplete = false) {
      return this.savePage({
        survey: this.surveyResponse.id,
        auth: this.surveyResponse.auth_id,
        page: this.activePage.id,
        model,
        form,
        skipPageChange,
        skipComplete,
      });
    },
  },

  created() {
    if (this.activeFields) {
      this.buildForm(this.formBuilderOptions || {});
    }
  },
};
